import React, { useCallback, useEffect, useState } from "react";
import ConfirmationModal from "../components/ConfirmationModal";
import { TableActionColumnHeader } from "../components/TableShell";
import { useNavigate, useParams } from "react-router-dom";
import { IconEdit, IconDelete, IconSave, IconCancel, iconBtnStyle, RowActionIcons } from "../shared/icons/IconLib";

import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require('../uiConfig');
const { getMessageForFetchError } = require('../shared/utils/apiError');
const ErrorBanner = require('../components/ErrorBanner').default;

function SolutionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
  const account_id = userInfo.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;

  const [solution, setSolution] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [bannerError, setBannerError] = useState('');
  const [workflowForm, setWorkflowForm] = useState({ name: '', details: '' });
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [workflowEditDraft, setWorkflowEditDraft] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    mode: "confirm", // "confirm" | "alert"
    onConfirm: null,
  });

  const loadSolution = useCallback(() => {
    if (!account_id) return;
    apiFetch(serverUrl + '/getSolution', {
      method: 'POST',
      body: JSON.stringify({ account_id }),
    })
      .then(resp => resp.json())
      .then(data => {
        const list = Array.isArray(data) ? data : [];
        const found = list.find(s => String(s._id) === String(id));
        setSolution(found || null);
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }, [account_id, serverUrl, id]);

  const loadWorkflows = useCallback(() => {
    if (!account_id || !id) return;
    apiFetch(serverUrl + '/getSolutionWorkflows', {
      method: 'POST',
      body: JSON.stringify({ account_id, solution_id: id }),
    })
      .then(resp => resp.json())
      .then(data => setWorkflows(Array.isArray(data) ? data : []))
      .catch(() => setWorkflows([]));
  }, [account_id, serverUrl, id]);

  useEffect(() => {
    loadSolution();
  }, [loadSolution]);

  useEffect(() => {
    if (id) loadWorkflows();
  }, [id, loadWorkflows]);

  function createWorkflow() {
    const nameVal = (workflowForm.name || '').trim();
    if (!nameVal) {
      setConfirmDialog({ open: true, title: "Workflow", message: "Name is required.", mode: "alert", onConfirm: null });
      return;
    }
    apiFetch(serverUrl + '/createSolutionWorkflow', {
      method: 'POST',
      body: JSON.stringify({
        account_id,
        solution_id: id,
        name: nameVal,
        details: (workflowForm.details || '').trim(),
      }),
    })
      .then(r => r.text())
      .then(text => {
        setConfirmDialog({ open: true, title: "Workflow", message: text, mode: "alert", onConfirm: null });
        loadWorkflows();
        setWorkflowForm({ name: '', details: '' });
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function updateWorkflow(wfId, payload) {
    const nameVal = (payload.name != null ? String(payload.name) : '').trim();
    if (!nameVal) {
      setConfirmDialog({ open: true, title: "Workflow", message: "Name is required.", mode: "alert", onConfirm: null });
      return;
    }
    apiFetch(serverUrl + '/updateSolutionWorkflow', {
      method: 'POST',
      body: JSON.stringify({ account_id, _id: wfId, name: payload.name, details: payload.details }),
    })
      .then(r => r.text())
      .then(text => {
        setConfirmDialog({ open: true, title: "Workflow", message: text, mode: "alert", onConfirm: null });
        loadWorkflows();
        setEditingWorkflowId(null);
        setWorkflowEditDraft(null);
      })
      .catch(err => setBannerError(getMessageForFetchError(err)));
  }

  function deleteWorkflow(wfId) {
    setConfirmDialog({
      open: true,
      title: "Delete workflow",
      message: "Delete this workflow?",
      onConfirm: () => {
        apiFetch(serverUrl + '/deleteSolutionWorkflow', {
          method: 'POST',
          body: JSON.stringify({ account_id, _id: wfId }),
        })
          .then(r => r.text())
          .then(text => {
            setConfirmDialog({ open: true, title: "Workflow", message: text, mode: "alert", onConfirm: null });
            loadWorkflows();
          })
          .catch(err => setBannerError(getMessageForFetchError(err)));
      },
    });
  }

  function startEditWorkflow(wf) {
    setEditingWorkflowId(wf._id);
    setWorkflowEditDraft({
      name: wf.name || '',
      details: wf.details || '',
    });
  }

  if (solution === undefined) {
    return (
      <div className="page-container">
        <div className="page-content">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
          <p className="empty-message">Loading…</p>
        </div>
      </div>
    );
  }

  if (solution === null) {
    return (
      <div className="page-container">
        <div className="page-content">
          <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
          <p className="empty-message">Solution not found.</p>
          <button type="button" onClick={() => navigate('/Solutions')} className="btn btn-secondary">Back to Solutions</button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-content page-container-full">
        <div className="page-top">
          <button type="button" onClick={() => navigate('/Solutions')} className="btn-link">← Solutions</button>
          <h1 className="page-title">Solution details</h1>
        </div>
        <ErrorBanner message={bannerError} onDismiss={() => setBannerError('')} />
        <ConfirmationModal
          open={confirmDialog.open}
          title={confirmDialog.title || "Confirm"}
          message={confirmDialog.message}
          mode={confirmDialog.mode === "alert" ? "alert" : "confirm"}
          confirmLabel={confirmDialog.mode === "alert" ? "OK" : undefined}
          onConfirm={() => {
            const fn = confirmDialog.onConfirm;
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null });
            if (fn) fn();
          }}
          onCancel={() =>
            setConfirmDialog({ open: false, title: "", message: "", mode: "confirm", onConfirm: null })
          }
        />
        <div className="page-section">
          <div className="panel-row" style={{ flexWrap: 'wrap', gap: '16px 24px' }}>
            <span><span className="panel-hint">Name:</span> {solution.name}</span>
            <span><span className="panel-hint">Details:</span> {solution.details || '—'}</span>
          </div>

          <h2 className="page-section-title">Workflows</h2>
          <div className="panel-row">
            <input type="text" value={workflowForm.name} onChange={(e) => setWorkflowForm(f => ({ ...f, name: e.target.value }))} placeholder="Name" className="input input-flex-name" />
            <input type="text" value={workflowForm.details} onChange={(e) => setWorkflowForm(f => ({ ...f, details: e.target.value }))} placeholder="Details" className="input input-flex-desc" />
            <button type="button" onClick={createWorkflow} className="btn btn-primary">Add</button>
          </div>
          <div className="table-scroll">
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Details</th>
                    <TableActionColumnHeader align="left" />
                  </tr>
                </thead>
                <tbody>
                  {workflows.map(wf => {
                    const isEditing = editingWorkflowId === wf._id;
                    const draft = isEditing ? workflowEditDraft : null;
                    return (
                      <tr key={wf._id} className={isEditing ? 'row-editing' : ''}>
                        {isEditing && draft ? (
                          <>
                            <td><input type="text" value={draft.name} onChange={(e) => setWorkflowEditDraft(d => ({ ...d, name: e.target.value }))} placeholder="Name" className="input input-inline-edit input-full" /></td>
                            <td><input type="text" value={draft.details} onChange={(e) => setWorkflowEditDraft(d => ({ ...d, details: e.target.value }))} placeholder="Details" className="input input-inline-edit input-full" /></td>
                            <td>
                              <button type="button" onClick={() => updateWorkflow(wf._id, workflowEditDraft)} className="btn-link" style={iconBtnStyle} title="Save"><IconSave size={18} /></button>
                              <button type="button" onClick={() => { setEditingWorkflowId(null); setWorkflowEditDraft(null); }} className="btn-link-muted" style={iconBtnStyle} title="Cancel"><IconCancel size={18} /></button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{wf.name}</td>
                            <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }} title={wf.details}>{wf.details || '—'}</td>
                            <td>
                              <RowActionIcons>
                                <button type="button" onClick={() => startEditWorkflow(wf)} className="btn-link" style={iconBtnStyle} title="Edit"><IconEdit size={18} /></button>
                                <button type="button" onClick={() => deleteWorkflow(wf._id)} className="btn-link-muted" style={iconBtnStyle} title="Delete"><IconDelete size={18} /></button>
                              </RowActionIcons>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {workflows.length === 0 && <p className="empty-message">No workflows. Add one above.</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default SolutionDetail;
